import type { LucideIcon } from 'lucide-react';
import {
  Award,
  BarChart3,
  BookOpen,
  Building2,
  Calendar,
  ClipboardList,
  FileSpreadsheet,
  Flag,
  Heart,
  LayoutDashboard,
  Library,
  Lightbulb,
  Megaphone,
  PenLine,
  ScrollText,
  Settings,
  ShieldCheck,
  Tags,
  Trophy,
  UserCog,
  Users,
  UsersRound,
} from 'lucide-react';
import { SYSTEM_ROLES, type PermissionKey } from '@lms/shared';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Hidden unless the user holds ALL of these. Absent = always visible. */
  requires?: PermissionKey[];
  /**
   * Hidden unless the user holds AT LEAST ONE of these.
   *
   * For a screen that hosts several independently-permissioned operations —
   * bulk, where somebody may be allowed to assign courses in bulk without
   * being allowed to import users.
   */
  requiresAny?: PermissionKey[];
  /**
   * Hidden from anybody whose roles are ALL listed here.
   *
   * The same escape hatch as `NavSection.hiddenForRoles`, for a single entry.
   * Used where a permission genuinely cannot express the rule — see the note
   * on My Departments, which an Admin holds the permission for and should not
   * be offered.
   */
  hiddenForRoles?: string[];
  /** Rendered but disabled, for anything not yet built. */
  comingSoon?: boolean;
  /** Which live count to show as a badge, if any. */
  count?: 'myCourses' | 'certificates' | 'wishlist';
  /**
   * Kept at the top of an Admin's menu rather than folded into "Employee
   * view" — see `visibleSections`. Only the Dashboard, which is a different
   * page for them entirely.
   */
  staysOnTop?: boolean;
  /**
   * Where this item goes when its section is dropped from an Admin's menu.
   *
   * Content is dropped for them because the dashboard's action panel covers
   * every entry in it — except this one, which has no card there and would
   * simply be gone. Named as a destination rather than a boolean so the rule
   * says what it does, and so a second orphan can land somewhere else.
   */
  keepUnder?: string;
}

/** The counts the sidebar can show. Real figures, from `/dashboard/nav-counts`. */
export interface NavCounts {
  myCourses: number;
  certificates: number;
  wishlist: number;
}

export interface NavSection {
  title: string;
  items: NavItem[];
  /**
   * Hidden from anybody whose roles are ALL listed here.
   *
   * The one exception to this file's rule, and used sparingly. Everything else
   * is decided by permission, so a custom role gets the right menu with no
   * code change — but "a Manager should not be shown this section" is not a
   * statement about capability, and expressing it as one would take the same
   * screens from every custom role holding those permissions.
   *
   * Read as "all of them", not "any of them", so somebody who is a Manager AND
   * an Admin still sees it.
   */
  hiddenForRoles?: string[];
  /**
   * Folded into a single "Employee view" section for an Admin.
   *
   * These three sections are somebody's own learning. For an Employee or a
   * Manager they ARE the job and stay exactly where they are; for an Admin
   * they are a side door into the product as their staff experience it, and
   * spreading them over three headings above the work pushed Manage, Content
   * and Insights below the fold.
   */
  employeeFacing?: boolean;
  /**
   * Dropped from an Admin's menu, because their dashboard already carries it.
   *
   * Only for a section whose every entry has a card on that dashboard —
   * either a permanent one in Quick actions or a task in "What needs your
   * attention". Any entry with neither must name a `keepUnder` destination, and
   * the test suite proves nothing is lost either way.
   *
   * A Manager keeps the section: they have no such panel, and this would take
   * their courses, assessments and marking queue off their menu with nothing
   * offered in exchange.
   */
  onDashboard?: boolean;
}

/**
 * The sidebar is derived from permissions, not role names — so a custom role
 * automatically gets the right menu without any code change.
 *
 * This only controls what is RENDERED. Every route behind it is independently
 * enforced by the API, so a hidden link is a convenience, not a control.
 */
/**
 * Whether this person has two jobs, and therefore two menus.
 *
 * Exported so the shell's view SWITCH and this menu's FOLDING are one
 * decision. They were two — `canSwitchView` in `AppShell` and an
 * `includes(ADMIN)` check here — and they disagreed the moment a third
 * organisation-wide role existed: the switch appeared and changed nothing.
 */
export function hasTwoViews(roleSlugs: readonly string[]): boolean {
  return roleSlugs.includes(SYSTEM_ROLES.ADMIN) || roleSlugs.includes(SYSTEM_ROLES.TRAINER);
}

export const NAV_SECTIONS: NavSection[] = [
  {
    /** Where the work is. Everything somebody opens to actually learn. */
    title: 'Learn',
    employeeFacing: true,
    items: [
      // The one entry that is NOT folded away for an Admin — their dashboard
      // is a different page, and it is the first thing they want.
      { label: 'Dashboard', to: '/', icon: LayoutDashboard, staysOnTop: true },
      /**
       * The Action center had an entry here and no longer does.
       *
       * The dashboard's "What needs your attention" panel grew to cover every
       * queue it summarised — marking, objections, flagged certificates,
       * overdue training by department — so the menu entry became a second
       * door to a room you were already standing in.
       *
       * The PAGE is still there, and still worth reaching: it lists the
       * overdue people by name and everyone sitting on a failed paper, which
       * no summary card can. Two tasks in that panel link to it, and both
       * appear exactly when it has something on it — so it is reachable
       * whenever it is worth opening, and absent when it would be empty.
       */
      { label: 'My Courses', to: '/my-courses', icon: BookOpen, requires: ['learning.view_own'], count: 'myCourses' },
      { label: 'Catalog', to: '/catalogue', icon: Library, requires: ['learning.browse_catalogue'] },
      { label: 'Assessments', to: '/my-assessments', icon: ClipboardList, requires: ['assessment.take'] },
    ],
  },
  {
    /**
     * What somebody has already done.
     *
     * Calendar and Announcements used to sit here too, which made the section
     * mean "everything that is not a course" rather than anything. Neither is
     * a record of progress — they are what is coming — so they moved to Plan
     * and this reads as one idea again.
     */
    title: 'Progress',
    employeeFacing: true,
    items: [
      // Two entries, not one. Certificates are a record you send to someone;
      // achievements are how you are doing. Sharing a screen made the formal
      // document compete for room with a badge grid.
      { label: 'Certificates', to: '/my-certificates', icon: Award, count: 'certificates' },
      { label: 'Achievements', to: '/achievements', icon: Trophy },
    ],
  },
  {
    /** What is ahead: dates, saved courses, and word from the organisation. */
    title: 'Plan',
    employeeFacing: true,
    items: [
      { label: 'Calendar', to: '/calendar', icon: Calendar, requires: ['calendar.view'] },
      { label: 'Wishlist', to: '/wishlist', icon: Heart, requires: ['learning.wishlist'], count: 'wishlist' },
      { label: 'Announcements', to: '/announcements', icon: Megaphone, requires: ['announcement.view'] },
    ],
  },
  {
    title: 'Manage',
    /**
     * Off the Manager's menu entirely, by request — Users, Departments,
     * Designations and Bulk Operations together.
     *
     * Nothing about what a Manager can DO has changed: they still hold
     * `user.view`, `department.view`, `designation.view` and both bulk-assign
     * permissions, and the API still scopes each of them to the departments
     * they govern. This only takes the section off their sidebar. Removing the
     * permissions instead would have been the larger change, and was not what
     * was asked for.
     */
    hiddenForRoles: [SYSTEM_ROLES.MANAGER],
    items: [
      { label: 'Users', to: '/admin/users', icon: Users, requires: ['user.view'] },
      { label: 'Roles & Permissions', to: '/admin/roles', icon: ShieldCheck, requires: ['role.view'] },
      { label: 'Departments', to: '/admin/departments', icon: Building2, requires: ['department.view'] },
      { label: 'Designations', to: '/admin/designations', icon: UserCog, requires: ['designation.view'] },
      {
        label: 'Bulk Operations',
        to: '/admin/bulk',
        icon: FileSpreadsheet,
        // Five operations share this screen and each has its own permission;
        // any one of them is reason enough to see it.
        requiresAny: [
          'user.import',
          'course.bulk_assign',
          'assessment.bulk_assign',
          'question.import',
          'certificate.bulk_issue',
        ],
      },
    ],
  },
  {
    title: 'Content',
    /**
     * Off a Manager's menu entirely, by request — and this one needed the
     * dashboard to change first.
     *
     * Their dashboard now carries a Quick actions panel holding exactly the
     * entries a Manager could see here: Courses, Assessments, the Evaluation
     * Queue and Question Reports. Unlike the Admin's attention tasks, those
     * tiles are PERMANENT — they do not come and go with a queue — so this is
     * a move rather than a removal, and `navigation.test.ts` checks the two
     * lists against each other rather than trusting this sentence.
     *
     * Categories is the one that does not move across, deliberately: see the
     * entry itself. Learning Spark and Certificates never appeared for a
     * Manager at all, who holds neither permission.
     *
     * ── And off a Trainer's menu too, also by request ─────────────────────
     * A Trainer lands on the Admin dashboard, whose Quick actions panel
     * carries Courses, Assessments and Certificates, and whose attention band
     * carries the Evaluation Queue and Learning Spark as permanent rows. So
     * five of the seven entries have another route.
     *
     * Two do not, and the difference is worth stating rather than discovering:
     *
     *   Question Reports  appears in the attention band only while a report is
     *                     open. With none open there is no route — which is
     *                     arguably right, since there is also nothing to do.
     *
     *   Categories        has no other route at all, and a Trainer HOLDS
     *                     `category.manage`. This is the one place the
     *                     removal costs a capability rather than relocating
     *                     it. Left as asked; withholding the permission, or
     *                     keeping this single entry, would each be a larger
     *                     change than was requested.
     */
    hiddenForRoles: [SYSTEM_ROLES.MANAGER, SYSTEM_ROLES.TRAINER],
    /**
     * Dropped for an Admin, because their dashboard carries all but one entry.
     *
     * Two different kinds of cover, and the difference is worth knowing before
     * adding an entry here. Courses, Assessments and Certificates have a
     * PERMANENT card in Quick actions. Learning Spark, the Evaluation Queue
     * and Question Reports have an ATTENTION TASK, which is conditional — "3
     * objections open" is not rendered when there are none, so an Admin whose
     * queues are clear reaches those three from nowhere.
     *
     * That is accepted: when there is nothing waiting there is nothing to open,
     * and a standing menu entry beside a dashboard card that says the same
     * thing is the same link twice. Categories has neither kind of card and so
     * names a `keepUnder` destination.
     */
    onDashboard: true,
    items: [
      /**
       * Courses, then Assessments, then Categories — the two things people
       * come here to build, then the list that supports them. Categories sat
       * between them, splitting the pair.
       */
      { label: 'Courses', to: '/admin/courses', icon: BookOpen, requires: ['course.view'] },
      { label: 'Assessments', to: '/admin/assessments', icon: ClipboardList, requires: ['assessment.view'] },
      /**
       * The one Content entry with no card on the dashboard.
       *
       * A list you set up once and rarely revisit does not earn a standing
       * shortcut there — so when Content is dropped for an Admin this moves
       * to Manage, beside the other lists that describe the organisation
       * rather than its learning.
       *
       * ── And off a Manager's menu entirely ──────────────────────────────
       * They hold `category.view` and not `category.manage`, deliberately —
       * one shared org-wide list, where a rename lands on all 30 departments.
       * So the screen opens for them with every button disabled: a list they
       * can read and nothing they can do about it.
       *
       * The PERMISSION stays, and must: `GET /categories` is behind it, and
       * that is the call the category dropdowns in the course and assessment
       * builders make. Taking it away would hide the screen by breaking those
       * pickers — which is why this is a menu rule and not a permission
       * change.
       */
      {
        label: 'Categories',
        to: '/admin/categories',
        icon: Tags,
        requires: ['category.view'],
        hiddenForRoles: [SYSTEM_ROLES.MANAGER],
        keepUnder: 'Manage',
      },
      /*
        No `keepUnder` on these three, by decision: an Admin reaches all of
        them from "What needs your attention", and duplicating them in the
        menu puts the same three links two inches apart. A Manager keeps them
        here, where they sit beside the content they are about.
      */
      { label: 'Learning Spark', to: '/admin/learning-spark', icon: Lightbulb, requires: ['challenge.view'] },
      { label: 'Evaluation Queue', to: '/admin/evaluation', icon: PenLine, requires: ['evaluation.view_queue'] },
      { label: 'Question Reports', to: '/admin/question-reports', icon: Flag, requires: ['rebuttal.view'] },
      { label: 'Certificates', to: '/admin/certificates', icon: Award, requires: ['certificate.view'] },
    ],
  },
  {
    title: 'Insights',
    items: [
      /**
       * A manager's own team, first in Insights.
       *
       * It sat in the Learn section, directly under Dashboard, where it landed
       * when the Manage section was taken off a manager's menu — position
       * rather than meaning. Learn is somebody's own learning, and this is
       * other people's: it belongs beside Reports, which is the same question
       * asked with numbers.
       *
       * `scope.department` is the permission, but it cannot decide this alone:
       * an Admin holds every scope — deliberately, so the escalation guard
       * lets them create Employees — and would get a "My departments" entry
       * leading to an organisation-wide view. Their version of this panel is
       * on their dashboard under its own heading, so it is hidden from them by
       * role. Somebody who is BOTH still sees it, because `hiddenForRoles`
       * needs every role held to be on the list.
       */
      {
        label: 'My departments',
        to: '/my-departments',
        icon: UsersRound,
        requires: ['scope.department'],
        hiddenForRoles: [SYSTEM_ROLES.ADMIN],
      },
      { label: 'Reports', to: '/admin/reports', icon: BarChart3, requires: ['report.view'] },
      /**
       * The staff, as opposed to the learners.
       *
       * Every other entry under Insights measures people being trained.
       * This measures the people doing the training and running the teams,
       * which is the one thing no other screen answers.
       *
       * Hidden from a Manager: they hold `report.view` for their own
       * department's figures, and a list of every trainer and manager in the
       * organisation is not theirs to read. An Admin's own two roles are not
       * on it either — the report covers Trainer and Manager.
       */
      {
        label: 'Leaders activity',
        to: '/admin/leaders-activity',
        icon: UserCog,
        requires: ['report.view'],
        hiddenForRoles: [SYSTEM_ROLES.MANAGER, SYSTEM_ROLES.TRAINER],
      },
      { label: 'Audit Log', to: '/admin/audit', icon: ScrollText, requires: ['audit.view'] },
      { label: 'Settings', to: '/admin/settings', icon: Settings, requires: ['settings.view'] },
    ],
  },
];

export function visibleSections(
  permissions: string[],
  roleSlugs: string[] = [],
  /**
   * Which of an administrator's two jobs they are currently doing.
   *
   * Ignored for everybody else — nobody else has two. Defaults to `admin` so
   * callers that only know permissions keep the menu they always had.
   */
  viewMode: 'admin' | 'employee' = 'admin',
): NavSection[] {
  const held = new Set(permissions);

  /**
   * Hidden only when EVERY role the person holds is on the list.
   *
   * Somebody who is both a Manager and an Admin keeps the section: the point
   * is to tidy the Manager's menu, not to take screens away from an
   * administrator who happens to manage a department too. A user with no roles
   * at all holds no permissions either, so the check never reaches them — but
   * the length guard says so rather than relying on it.
   */
  const hiddenFrom = (entry: { hiddenForRoles?: string[] }): boolean =>
    Boolean(entry.hiddenForRoles) &&
    roleSlugs.length > 0 &&
    roleSlugs.every((slug) => entry.hiddenForRoles!.includes(slug));

  /**
   * Items first, sections second — and that order is load-bearing.
   *
   * An entry relocated out of a hidden section must already have been checked
   * against its OWN permissions and role rules, or a Manager would inherit
   * Categories out of the Content section they are not shown.
   */
  const withVisibleItems = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        (!item.requires || item.requires.every((key) => held.has(key))) &&
        (!item.requiresAny || item.requiresAny.some((key) => held.has(key))) &&
        !hiddenFrom(item),
    ),
  })).filter((section) => section.items.length > 0);

  /**
   * A section hidden BY ROLE still gives up its `keepUnder` entries.
   *
   * This dropped the section whole, which cost a Trainer their only route to
   * Categories: Content is hidden for them, and Categories is the one entry
   * in it with no dashboard card to fall back on — so a permission they
   * genuinely hold, `category.manage`, had nowhere it could be used from. The
   * screen was reachable only by typing the URL.
   *
   * `keepUnder` already means "where this goes when its section is dropped".
   * A section dropped by role is the same event as one dropped because the
   * dashboard covers it, so it obeys the same rule — and "nothing is lost"
   * now holds for both ways a section can disappear rather than just one.
   */
  const orphans: NavItem[] = [];
  const visible = withVisibleItems.filter((section) => {
    if (!hiddenFrom(section)) return true;
    orphans.push(...section.items.filter((item) => item.keepUnder));
    return false;
  });

  const sections = relocate(visible, orphans);

  /**
   * Only a role with TWO views gets a folded menu.
   *
   * This read `includes(ADMIN)` and nothing else, which was true until the
   * Trainer arrived. A Trainer was handed the view switch and fell through to
   * here, where the raw list came back — so their working menu carried Learn,
   * Progress and Plan as well, duplicating the employee view they could
   * already switch to. The switch existed and the menu ignored it.
   */
  if (!hasTwoViews(roleSlugs)) {
    return roleSlugs.includes(SYSTEM_ROLES.MANAGER) ? managerMenu(sections) : sections;
  }
  return viewMode === 'employee' ? employeeMenu(sections) : adminMenu(sections);
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * A MANAGER'S MENU
 *
 * One change: Insights moves up to sit directly under Learn.
 *
 * It is last in `NAV_SECTIONS` because that is where it belongs for an
 * Admin, whose menu runs Manage → Content → Insights and who opens the
 * product to administer it. A Manager's two entries there — their own team,
 * and the reports about it — are the first thing they came for, and sat below
 * Progress, Plan and their own wishlist.
 *
 * Done here rather than by reordering the array, because the array is the
 * Admin's order and "everything else stays as it is" was the ask. By role
 * rather than by permission, and it is the same judgement as `hiddenForRoles`:
 * "a manager opens this product to run their team" is a statement about a job,
 * not about a capability, and writing it as a permission would reorder the
 * menu of every custom role that happens to hold `report.view`.
 * ═══════════════════════════════════════════════════════════════════════════
 */
function managerMenu(sections: NavSection[]): NavSection[] {
  const insights = sections.find((section) => section.title === 'Insights');
  if (!insights) return sections;

  const rest = sections.filter((section) => section !== insights);
  const learnAt = rest.findIndex((section) => section.title === 'Learn');

  /* No Learn section to sit under — nothing sensible to do but leave it. */
  if (learnAt === -1) return sections;

  return [...rest.slice(0, learnAt + 1), insights, ...rest.slice(learnAt + 1)];
}

/**
 * An administrator looking at the product as their staff see it.
 *
 * EXACTLY the employee menu — Learn, Progress, Plan, in their own order, with
 * nothing added. That is the whole value: an administrator checking "what does
 * my team see on this screen" gets a truthful answer, and a nearly-right menu
 * with one admin entry left in it would answer the question wrongly in the one
 * way that matters.
 *
 * The Dashboard comes along, because it is in the Learn section and an
 * employee has one. `DashboardPage` renders the learner's version in this
 * mode, so it is the same entry leading to the same page they would see.
 */
function employeeMenu(sections: NavSection[]): NavSection[] {
  /**
   * Scopes that mean "this entry is about OTHER PEOPLE".
   *
   * The learner sections are not purely learner entries: Action center and My
   * departments both sit in Learn, positioned under Dashboard, and neither is
   * something an employee has ever seen. Filtering the SECTION alone left them
   * in and quietly made employee view a lie.
   *
   * Expressed as the scopes rather than as a list of the two offending
   * labels, so the next management entry parked in a learner section is
   * handled without anybody remembering this rule exists.
   */
  const MANAGEMENT_SCOPES = ['scope.organization', 'scope.department'];

  return sections
    .filter((section) => section.employeeFacing)
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.requires?.some((key) => MANAGEMENT_SCOPES.includes(key)),
      ),
    }))
    .filter((section) => section.items.length > 0);
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AN ADMIN'S MENU, FOR AN ADMIN ONLY
 *
 * An administrator's own learning is real — mandatory training is mandatory
 * for the person who administers it too — but it is not what they open the
 * product to do. Spread across Learn, Progress and Plan it took three headings
 * and eight entries at the TOP of the menu, so Manage, Content and Insights
 * all began below them, and on a short window Settings needed a scroll.
 *
 * Folded into one section at the bottom, their menu opens on their work.
 * Nothing is removed and no permission changes: every entry is still there,
 * under one label, one click from where it was.
 *
 * By role rather than by permission, deliberately, and it is the same
 * judgement as `hiddenForRoles` above: "an administrator wants their own
 * learning gathered in one place" is a statement about a job, not about a
 * capability, and writing it as a permission would reorder the menu of every
 * custom role that happens to hold organisation scope.
 * ═══════════════════════════════════════════════════════════════════════════
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * NOTHING IS LOST WHEN A SECTION GOES
 *
 * Two different things drop a section — a role it is hidden from, and a
 * dashboard that already carries it — and both leave the same problem behind:
 * an entry whose only route was that heading. `keepUnder` names where such an
 * entry goes instead, and this is the one place that promise is kept, so the
 * two callers cannot drift apart.
 *
 * Arrivals are appended after whatever the destination already holds, because
 * they are the newcomers and the section's own order was chosen.
 *
 * A destination that is not present keeps its OWN section rather than quietly
 * swallowing the entry. That is not merely defensive: a Trainer holds none of
 * Manage's permissions, so Manage is not in their menu at all, and Categories
 * arriving there is exactly the case that used to vanish.
 * ═══════════════════════════════════════════════════════════════════════════
 */
function relocate(sections: NavSection[], orphans: NavItem[]): NavSection[] {
  if (orphans.length === 0) return sections;

  const waiting = new Map<string, NavItem[]>();
  for (const item of orphans) {
    /* Only `keepUnder` entries are ever passed in — both callers filter. */
    const destination = item.keepUnder!;
    waiting.set(destination, [...(waiting.get(destination) ?? []), item]);
  }

  const withArrivals = sections.map((section) => {
    const arrivals = waiting.get(section.title);
    if (!arrivals) return section;
    waiting.delete(section.title);
    return { ...section, items: [...section.items, ...arrivals] };
  });

  return [
    ...withArrivals,
    ...[...waiting.entries()].map(([title, items]) => ({ title, items })),
  ];
}

function adminMenu(sections: NavSection[]): NavSection[] {
  const pinned: NavItem[] = [];
  const kept: NavSection[] = [];
  /** Entries from a dropped section, to be placed by `relocate` below. */
  const orphans: NavItem[] = [];

  for (const section of sections) {
    if (section.employeeFacing) {
      /**
       * Only the pinned entries survive into the admin menu; the rest are
       * reached by switching to employee view.
       *
       * They used to be folded into an "Employee view" SECTION at the bottom
       * of this menu. The switch beside the user card does that job now, and
       * better — it changes the dashboard too, so the answer to "what does my
       * team see" is the whole screen rather than a list of links. Keeping
       * both would put the same eight entries in two places.
       */
      for (const item of section.items) {
        if (item.staysOnTop) pinned.push(item);
      }
      continue;
    }

    if (section.onDashboard) {
      orphans.push(...section.items.filter((item) => item.keepUnder));
      continue;
    }

    kept.push(section);
  }

  return [
    ...(pinned.length > 0 ? [{ title: 'Overview', items: pinned }] : []),
    ...relocate(kept, orphans),
  ];
}
